// src/utils/bookingLifecycle.js
// LOOP PHASE 2.8 — Inventory Automation Bridge
// LOOP PHASE 2.9 — Inventory Automation Bridge in reservation lifecycle
// LOOP PHASE 2.6 — Daily Cash Closing compatible lifecycle helpers
// LOOP PHASE 2.5C — Canonical Booking Lifecycle
// Single source of truth for reservation status, attendance, payment and collected amounts.

export function lifecycleNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function normalizeBookingStatus(value, fallback = "confirmed") {
  const raw = String(value || "").trim().toLowerCase();
  if (["approved", "accepted", "confirmada", "confirmed", "aprobada"].includes(raw)) return "confirmed";
  if (["pendiente", "pending", "to_confirm", "por_confirmar", "requested"].includes(raw)) return "pending";
  if (["started", "in-progress", "in_progress", "en_progreso"].includes(raw)) return "in_progress";
  if (["done", "complete", "completed", "completada"].includes(raw)) return "completed";
  if (["no-show", "noshow", "no_show", "no asistió", "no_asistio", "no_asistio"].includes(raw)) return "no_show";
  if (["cancelled", "canceled", "cancelada"].includes(raw)) return "cancelled";
  if (["rejected", "declined", "rechazada"].includes(raw)) return "rejected";
  return raw || fallback;
}

export function normalizeAttendanceStatus(value, bookingStatus = "confirmed") {
  const raw = String(value || "").trim().toLowerCase();
  if (["attended", "asistio", "asistió", "present", "completed", "in_progress"].includes(raw)) return "attended";
  if (["no_show", "noshow", "no-show", "no asistio", "no asistió", "absent"].includes(raw)) return "no_show";
  if (["not_applicable", "cancelled", "rejected"].includes(raw)) return "not_applicable";
  const status = normalizeBookingStatus(bookingStatus);
  if (status === "no_show") return "no_show";
  if (status === "completed" || status === "in_progress") return "attended";
  if (status === "cancelled" || status === "rejected") return "not_applicable";
  return "pending";
}

export function normalizePaymentStatus(value, amounts = {}) {
  const raw = String(value || "").trim().toLowerCase();
  if (["paid", "pagado", "cobrado", "complete", "completed"].includes(raw)) return "paid";
  if (["partial", "parcial", "partially_paid"].includes(raw)) return "partial";
  if (["refunded", "refund", "devuelto", "reembolsado"].includes(raw)) return "refunded";
  if (["covered", "cubierto", "membership", "membresia", "membresía", "loyalty_reward", "fidelidad", "reward", "recompensa", "courtesy", "cortesia", "cortesía", "free", "gratis"].includes(raw)) return "covered";
  if (["pending", "pendiente", "unpaid", "por_cobrar", "por cobrar"].includes(raw)) return "pending";

  const collected = lifecycleNumber(amounts.collected ?? amounts.serviceCollected ?? amounts.amountCharged, 0);
  const expected = lifecycleNumber(amounts.expected ?? amounts.expectedServiceAmount ?? amounts.totalDueAmount, 0);
  if (collected > 0 && expected > 0 && collected < expected) return "partial";
  if (collected > 0) return "paid";
  return "pending";
}

export function normalizePaymentMethod(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["cash", "efectivo"].includes(raw)) return "cash";
  if (["yappy", "japi", "yappi"].includes(raw)) return "yappy";
  if (["card", "tarjeta", "credit_card", "debit_card"].includes(raw)) return "card";
  if (["transfer", "transferencia", "bank_transfer"].includes(raw)) return "transfer";
  if (["membership", "membresia", "membresía"].includes(raw)) return "membership";
  if (["loyalty_reward", "loyalty", "fidelidad", "reward", "recompensa"].includes(raw)) return "loyalty_reward";
  if (["courtesy", "cortesia", "cortesía", "free", "gratis"].includes(raw)) return "courtesy";
  if (["other", "otro"].includes(raw)) return "other";
  return raw || "cash";
}

export function normalizeCashPaymentMethod(value, fallback = "unknown") {
  const raw = String(value || "").trim().toLowerCase();
  if (["cash", "efectivo"].includes(raw)) return "cash";
  if (["yappy", "japi", "yappi"].includes(raw)) return "yappy";
  if (["card", "tarjeta", "credit_card", "debit_card"].includes(raw)) return "card";
  if (["transfer", "transferencia", "bank_transfer"].includes(raw)) return "transfer";
  if (["other", "otro"].includes(raw)) return "other";
  return fallback;
}

export function normalizeTipPaymentMethod(value, fallback = "unknown") {
  return normalizeCashPaymentMethod(value, fallback);
}

export function getBookingProductSalesTotal(raw = {}) {
  const direct = lifecycleNumber(raw.productSalesTotalAmount ?? raw.retailSalesTotalAmount ?? raw.payment?.productSalesTotalAmount ?? 0, 0);
  if (direct > 0) return direct;
  const rows = Array.isArray(raw.productSales) ? raw.productSales : Array.isArray(raw.retailSales) ? raw.retailSales : [];
  return rows.reduce((sum, row) => {
    const qty = lifecycleNumber(row?.quantity ?? row?.qty, 0);
    const price = lifecycleNumber(row?.unitPrice ?? row?.price, 0);
    return sum + lifecycleNumber(row?.lineTotal, qty * price);
  }, 0);
}

export function resolveExpectedServiceAmount(raw = {}) {
  const explicit = raw.expectedServiceAmount ?? raw.serviceExpectedAmount ?? raw.payment?.expectedServiceAmount ?? raw.payment?.serviceExpectedAmount;
  const explicitN = lifecycleNumber(explicit, 0);
  if (explicitN > 0) return explicitN;

  const serviceCollected = resolveServiceCollectedAmount(raw, { allowExpectedFallback: false });
  const productSalesTotal = getBookingProductSalesTotal(raw);
  const candidates = [
    raw.servicePrice,
    raw.price,
    raw.basePrice,
    raw.totalPrice,
    raw.totalAmount,
    raw.total,
    raw.amount,
    raw.paymentAmount,
    raw.paidAmount,
    raw.amountCharged,
    raw.payment?.totalDueAmount,
    raw.payment?.expectedAmount,
    raw.payment?.amountCharged,
  ];

  for (const candidate of candidates) {
    const n = lifecycleNumber(candidate, 0);
    if (n > 0) return Math.max(0, n - productSalesTotal);
  }

  // If the service had no configured price but the business charged a value,
  // treat the charged value as the real service amount for this booking.
  return serviceCollected > 0 ? serviceCollected : 0;
}

export function resolveServiceCollectedAmount(raw = {}, options = {}) {
  const explicit =
    raw.serviceCollectedAmount ??
    raw.payment?.serviceCollectedAmount ??
    raw.amountCharged ??
    raw.payment?.amountCharged ??
    raw.paidAmount ??
    raw.payment?.paidAmount ??
    raw.paymentAmount ??
    raw.payment?.paymentAmount ??
    raw.amountPaid ??
    raw.payment?.amountPaid ??
    null;

  const explicitN = lifecycleNumber(explicit, 0);
  if (explicitN > 0) return explicitN;

  if (options.allowExpectedFallback === false) return 0;
  const status = normalizePaymentStatus(raw.paymentStatus || raw.payment?.status, { collected: 0, expected: 0 });
  if (status === "paid") return resolveExpectedServiceAmount(raw);
  return 0;
}

export function resolveCoveredAmount(raw = {}) {
  const explicit = lifecycleNumber(raw.coveredAmount ?? raw.payment?.coveredAmount ?? raw.benefitCoveredAmount ?? 0, 0);
  if (explicit > 0) return explicit;
  const status = normalizePaymentStatus(raw.paymentStatus || raw.payment?.status, { collected: 0, expected: 0 });
  if (status === "covered") return resolveExpectedServiceAmount(raw);
  return 0;
}

export function resolveCommissionBaseAmount(raw = {}) {
  const explicit = lifecycleNumber(raw.commissionBaseAmount ?? raw.payment?.commissionBaseAmount ?? 0, 0);
  if (explicit > 0) return explicit;
  const paymentStatus = normalizePaymentStatus(raw.paymentStatus || raw.payment?.status, { collected: resolveServiceCollectedAmount(raw, { allowExpectedFallback: false }), expected: resolveExpectedServiceAmount(raw) });
  if (paymentStatus === "covered") return resolveCoveredAmount(raw) || resolveExpectedServiceAmount(raw);
  const collected = resolveServiceCollectedAmount(raw, { allowExpectedFallback: false });
  if (collected > 0) return collected;
  return resolveExpectedServiceAmount(raw);
}

export function resolveTipAmount(raw = {}) {
  return lifecycleNumber(raw.tipAmount ?? raw.tip ?? raw.payment?.tipAmount ?? 0, 0);
}

export function resolveTotalCollectedAmount(raw = {}) {
  const explicit = lifecycleNumber(raw.totalCollectedAmount ?? raw.grossCollectedAmount ?? raw.payment?.totalCollectedAmount ?? raw.payment?.grossCollectedAmount ?? 0, 0);
  if (explicit > 0) return explicit;
  return resolveServiceCollectedAmount(raw) + resolveTipAmount(raw) + getBookingProductSalesTotal(raw);
}

export function deriveLifecycleStage({ bookingStatus, attendanceStatus, paymentStatus, serviceCollectedAmount, expectedServiceAmount } = {}) {
  const status = normalizeBookingStatus(bookingStatus);
  const attendance = normalizeAttendanceStatus(attendanceStatus, status);
  const payment = normalizePaymentStatus(paymentStatus, { collected: serviceCollectedAmount, expected: expectedServiceAmount });
  const collected = lifecycleNumber(serviceCollectedAmount, 0);
  const expected = lifecycleNumber(expectedServiceAmount, 0);

  if (status === "pending") return "needs_approval";
  if (status === "cancelled" || status === "rejected") return "lost";
  if (status === "no_show" || attendance === "no_show") return "no_show";
  if (status === "in_progress") return "in_service";
  if (status === "completed") {
    if (payment === "paid") return "completed_paid";
    if (payment === "covered") return "completed_covered";
    if (payment === "partial" || (collected > 0 && expected > collected)) return "completed_partial";
    return "completed_unpaid";
  }
  if (payment === "paid") return "upcoming_paid";
  if (payment === "covered") return "upcoming_covered";
  return "upcoming";
}

export function deriveCommissionLifecycleStatus({ bookingStatus, paymentStatus, commissionOnlyCompleted = true } = {}) {
  const status = normalizeBookingStatus(bookingStatus);
  const payment = normalizePaymentStatus(paymentStatus);

  if (["cancelled", "rejected", "no_show"].includes(status)) return "not_applicable";
  if (commissionOnlyCompleted && status !== "completed") {
    return payment === "paid" || payment === "covered" ? "held_until_completed" : "estimated";
  }
  if (status === "completed" && (payment === "paid" || payment === "covered")) return "payable";
  if (status === "completed" && payment === "partial") return "earned_pending_balance";
  if (status === "completed") return "earned_pending_collection";
  return "estimated";
}

export function commissionLifecycleLabel(value) {
  const raw = String(value || "estimated").trim().toLowerCase();
  const labels = {
    estimated: "Comisión estimada",
    held_until_completed: "Pago cobrado · comisión pendiente de completar",
    earned_pending_collection: "Comisión ganada · falta cobrar",
    earned_pending_balance: "Comisión ganada · saldo pendiente",
    payable: "Comisión lista para pagar",
    not_applicable: "Comisión no aplica",
  };
  return labels[raw] || labels.estimated;
}

export function bookingLifecycleFromRecord(raw = {}) {
  const bookingStatus = normalizeBookingStatus(raw.bookingStatus || raw.status || raw.confirmationStatus || raw.state || "confirmed");
  const attendanceStatus = normalizeAttendanceStatus(raw.attendanceStatus || raw.attendance || raw.customerAttendanceStatus, bookingStatus);
  const expectedServiceAmount = resolveExpectedServiceAmount(raw);
  const serviceCollectedAmount = resolveServiceCollectedAmount(raw);
  const coveredAmount = resolveCoveredAmount(raw);
  const commissionBaseAmount = resolveCommissionBaseAmount(raw);
  const tipAmount = resolveTipAmount(raw);
  const productSalesTotalAmount = getBookingProductSalesTotal(raw);
  const totalCollectedAmount = resolveTotalCollectedAmount(raw);
  const paymentStatus = normalizePaymentStatus(raw.paymentStatus || raw.payment?.status, {
    collected: serviceCollectedAmount,
    expected: expectedServiceAmount,
  });
  const paymentMethod = normalizePaymentMethod(raw.paymentMethod || raw.payment?.method || (paymentStatus === "covered" ? "membership" : "cash"));
  const tipPaymentMethod = normalizeTipPaymentMethod(raw.tipPaymentMethod || raw.payment?.tipPaymentMethod || raw.payment?.tipMethod || raw.workerTipPaymentMethod || raw.payment?.workerTipPaymentMethod || "", "unknown");
  const paymentCoverageType = raw.paymentCoverageType || raw.coverageType || raw.payment?.coverageType || (paymentStatus === "covered" ? paymentMethod : "cash");
  const lifecycleStage = deriveLifecycleStage({ bookingStatus, attendanceStatus, paymentStatus, serviceCollectedAmount, expectedServiceAmount });
  const commissionLifecycleStatus = deriveCommissionLifecycleStatus({
    bookingStatus,
    paymentStatus,
    commissionOnlyCompleted: raw.commissionOnlyCompleted !== false && raw.payment?.commissionOnlyCompleted !== false,
  });

  return {
    bookingStatus,
    status: bookingStatus,
    confirmationStatus: bookingStatus,
    attendanceStatus,
    paymentStatus,
    paymentMethod,
    tipPaymentMethod,
    expectedServiceAmount,
    serviceExpectedAmount: expectedServiceAmount,
    serviceCollectedAmount,
    coveredAmount,
    paymentCoverageType,
    commissionBaseAmount,
    amountCharged: serviceCollectedAmount,
    paidAmount: serviceCollectedAmount,
    paymentAmount: serviceCollectedAmount,
    tipAmount,
    tipPaymentMethod,
    productSalesTotalAmount,
    totalCollectedAmount,
    grossCollectedAmount: totalCollectedAmount,
    totalDueAmount: Math.max(0, expectedServiceAmount + productSalesTotalAmount + tipAmount),
    lifecycleStage,
    commissionLifecycleStatus,
    commissionLifecycleLabel: commissionLifecycleLabel(commissionLifecycleStatus),
  };
}

// LOOP PHASE 2.8 — Inventory Automation Bridge helpers.
export function normalizeInventoryDeductionStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (["deducted", "done", "completed", "descontado"].includes(raw)) return "deducted";
  if (["failed", "error", "insufficient_stock", "stock_error"].includes(raw)) return "failed";
  if (["skipped", "not_configured", "no_materials"].includes(raw)) return "not_configured";
  if (["pending", "ready", "por_descontar"].includes(raw)) return "pending";
  return raw || "pending";
}

export function hasInventoryDeducted(raw = {}) {
  const status = normalizeInventoryDeductionStatus(raw.inventoryDeductionStatus || raw.inventoryStatus || raw.inventory?.deductionStatus);
  return Boolean(raw.inventoryDeductedAt || raw.inventory?.deductedAt || status === "deducted");
}

export function bookingMeetsInventoryDeductionTrigger(raw = {}, settings = {}) {
  const lifecycle = bookingLifecycleFromRecord(raw || {});
  const nested = settings?.inventorySettings && typeof settings.inventorySettings === "object" ? settings.inventorySettings : {};
  const trigger = String(settings.deductTrigger || settings.inventoryDeductTrigger || nested.deductTrigger || "completed").trim().toLowerCase();
  if (["paid", "payment", "cobrado"].includes(trigger)) return lifecycle.paymentStatus === "paid";
  if (["completed_paid", "completed_and_paid"].includes(trigger)) return lifecycle.bookingStatus === "completed" && lifecycle.paymentStatus === "paid";
  return lifecycle.bookingStatus === "completed";
}

export function shouldAutoDeductInventoryForBooking(raw = {}, settings = {}) {
  const nested = settings?.inventorySettings && typeof settings.inventorySettings === "object" ? settings.inventorySettings : {};
  const moduleEnabled = settings?.enabledModules?.inventory !== false;
  const autoDeduct = settings.autoDeduct ?? settings.inventoryAutoDeduct ?? nested.autoDeduct ?? true;
  if (!moduleEnabled || nested.enabled === false || nested.plan === "disabled" || autoDeduct === false) return false;
  if (hasInventoryDeducted(raw)) return false;
  const status = normalizeBookingStatus(raw.bookingStatus || raw.status || raw.confirmationStatus || raw.state || "confirmed");
  if (["cancelled", "rejected", "no_show"].includes(status)) return false;
  return bookingMeetsInventoryDeductionTrigger(raw, settings);
}

export function buildBookingLifecyclePatch(raw = {}, patch = {}) {
  const merged = { ...(raw || {}), ...(patch || {}) };
  const lifecycle = bookingLifecycleFromRecord(merged);
  const payment = {
    ...(raw?.payment || {}),
    ...(patch?.payment || {}),
    status: lifecycle.paymentStatus,
    method: lifecycle.paymentMethod,
    serviceCollectedAmount: lifecycle.serviceCollectedAmount,
    amountCharged: lifecycle.serviceCollectedAmount,
    paidAmount: lifecycle.serviceCollectedAmount,
    paymentAmount: lifecycle.serviceCollectedAmount,
    tipAmount: lifecycle.tipAmount,
    tipPaymentMethod: lifecycle.tipPaymentMethod,
    productSalesTotalAmount: lifecycle.productSalesTotalAmount,
    totalCollectedAmount: lifecycle.totalCollectedAmount,
    grossCollectedAmount: lifecycle.grossCollectedAmount,
    expectedServiceAmount: lifecycle.expectedServiceAmount,
    serviceExpectedAmount: lifecycle.serviceExpectedAmount,
    coveredAmount: lifecycle.coveredAmount,
    coverageType: lifecycle.paymentCoverageType,
    paymentCoverageType: lifecycle.paymentCoverageType,
    commissionBaseAmount: lifecycle.commissionBaseAmount,
    totalDueAmount: lifecycle.totalDueAmount,
  };

  return {
    bookingStatus: lifecycle.bookingStatus,
    status: lifecycle.bookingStatus,
    confirmationStatus: lifecycle.confirmationStatus,
    attendanceStatus: lifecycle.attendanceStatus,
    customerAttendanceStatus: lifecycle.attendanceStatus,
    paymentStatus: lifecycle.paymentStatus,
    paymentMethod: lifecycle.paymentMethod,
    expectedServiceAmount: lifecycle.expectedServiceAmount,
    serviceExpectedAmount: lifecycle.serviceExpectedAmount,
    serviceCollectedAmount: lifecycle.serviceCollectedAmount,
    coveredAmount: lifecycle.coveredAmount,
    paymentCoverageType: lifecycle.paymentCoverageType,
    commissionBaseAmount: lifecycle.commissionBaseAmount,
    amountCharged: lifecycle.amountCharged,
    paidAmount: lifecycle.paidAmount,
    paymentAmount: lifecycle.paymentAmount,
    tipAmount: lifecycle.tipAmount,
    tipPaymentMethod: lifecycle.tipPaymentMethod,
    productSalesTotalAmount: lifecycle.productSalesTotalAmount,
    totalCollectedAmount: lifecycle.totalCollectedAmount,
    grossCollectedAmount: lifecycle.grossCollectedAmount,
    totalDueAmount: lifecycle.totalDueAmount,
    lifecycleStage: lifecycle.lifecycleStage,
    commissionLifecycleStatus: lifecycle.commissionLifecycleStatus,
    commissionLifecycleLabel: lifecycle.commissionLifecycleLabel,
    payment,
    bookingLifecycle: {
      bookingStatus: lifecycle.bookingStatus,
      attendanceStatus: lifecycle.attendanceStatus,
      paymentStatus: lifecycle.paymentStatus,
      paymentMethod: lifecycle.paymentMethod,
      expectedServiceAmount: lifecycle.expectedServiceAmount,
      serviceCollectedAmount: lifecycle.serviceCollectedAmount,
      coveredAmount: lifecycle.coveredAmount,
      paymentCoverageType: lifecycle.paymentCoverageType,
      commissionBaseAmount: lifecycle.commissionBaseAmount,
      tipAmount: lifecycle.tipAmount,
      tipPaymentMethod: lifecycle.tipPaymentMethod,
      productSalesTotalAmount: lifecycle.productSalesTotalAmount,
      totalCollectedAmount: lifecycle.totalCollectedAmount,
      lifecycleStage: lifecycle.lifecycleStage,
      commissionLifecycleStatus: lifecycle.commissionLifecycleStatus,
      commissionLifecycleLabel: lifecycle.commissionLifecycleLabel,
      updatedAtClient: new Date().toISOString(),
    },
  };
}
